import React, { useCallback, useState } from 'react';
import { Badge, Button, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { searchDirectoryPeople } from '../services/directorySearch';

/**
 * Who is told in Teams when a customer needs a person. Required on every
 * conversation from 29 Sep 2026: an unrecognised reply now stops the flow and
 * hands it over, so somebody must be named to hand it to. The server refuses
 * a start without one as well.
 *
 * Used by the Start Conversation dialog and by Karma Text Booking.
 */
export default function TeamsNotifyPicker({ instance, selected, onChange, idPrefix = 'teams' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [manualEmail, setManualEmail] = useState('');

  const add = useCallback((person) => {
    if (!person?.email) return;
    const exists = selected.some((item) => item.email.toLowerCase() === person.email.toLowerCase());
    if (!exists) onChange([...selected, { name: person.name || person.email, email: person.email }]);
  }, [selected, onChange]);

  const search = useCallback(async () => {
    const text = String(query || '').trim();
    if (text.length < 2) { setError('Type at least two characters.'); return; }
    setSearching(true); setError(''); setResults([]);
    try {
      const people = await searchDirectoryPeople(instance, text);
      setResults(people);
      if (!people.length) setError('Nobody matched that search.');
    } catch (err) {
      setError(err?.message || 'Could not search for colleagues.');
    } finally {
      setSearching(false);
    }
  }, [query, instance]);

  const addManual = useCallback(() => {
    const email = String(manualEmail || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError('That does not look like an email address.'); return; }
    add({ name: email, email }); setManualEmail(''); setError('');
  }, [manualEmail, add]);

  const onEnter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); fn(); } };

  return (
    <div>
      <InputGroup size="sm">
        <Form.Control id={`${idPrefix}-search`} placeholder="Search colleagues by name or email" value={query}
          onChange={(e) => setQuery(e.target.value)} onKeyDown={onEnter(search)} />
        <Button variant="outline-secondary" onClick={search} disabled={searching || query.trim().length < 2}>
          {searching ? 'Searching...' : <><FontAwesomeIcon icon={faSearch} /> Search</>}
        </Button>
      </InputGroup>
      {error && <div className="text-danger small mt-1">{error}</div>}
      {results.length > 0 && (
        <div className="border rounded mt-2 bg-white" style={{ maxHeight: '160px', overflowY: 'auto' }}>
          {results.map((person) => (
            <div key={person.email} className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom">
              <div className="me-2">
                <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{person.name}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {person.email}{person.jobTitle ? ` · ${person.jobTitle}` : ''}
                </div>
              </div>
              <Button size="sm" variant="outline-primary" onClick={() => add(person)}>Add</Button>
            </div>
          ))}
        </div>
      )}
      <InputGroup size="sm" className="mt-2">
        <Form.Control placeholder="...or type an email address" value={manualEmail}
          onChange={(e) => setManualEmail(e.target.value)} onKeyDown={onEnter(addManual)} />
        <Button variant="outline-secondary" onClick={addManual}>Add</Button>
      </InputGroup>
      {selected.length > 0 ? (
        <div className="mt-2">
          <div className="small fw-bold mb-1">Will be notified:</div>
          <div className="d-flex flex-wrap gap-1">
            {selected.map((person) => (
              <Badge bg="primary" key={person.email} className="d-flex align-items-center gap-1">
                {person.name}
                <span role="button" aria-label={`Remove ${person.name}`} style={{ cursor: 'pointer' }}
                  onClick={() => onChange(selected.filter((item) => item.email !== person.email))}>&times;</span>
              </Badge>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-danger small mt-2">Required: choose at least one person before sending.</div>
      )}
    </div>
  );
}
